import React from 'react';
import { Download, Save, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const Select = ({ value, onChange, children, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white" data-testid={testId}>{children}</select>
);

// Étape 1 : choix du fichier + modèles + aide colonnes
export const ImportFileStep = ({ fields, onFile, busy, templateUrl }) => (
  <div className="space-y-5" data-testid="import-step-file">
    <section className="bg-white rounded-xl shadow-sm p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900">1. Choisissez votre fichier</h3>
        <p className="text-sm text-gray-500 mt-1">CSV (virgule, point-virgule ou tabulation, encodage détecté automatiquement) ou Excel .xlsx — {fields?.limits?.max_mb} Mo et {fields?.limits?.max_rows?.toLocaleString('fr-FR')} lignes maximum. Le format .xls n’est pas accepté.</p>
      </div>
      <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 hover:border-orange-400 p-8 cursor-pointer text-center">
        <span className="text-sm font-medium text-gray-700">{busy ? 'Analyse en cours…' : 'Cliquez pour sélectionner un fichier'}</span>
        <span className="text-xs text-gray-400">.csv, .tsv, .txt, .xlsx</span>
        <input type="file" accept=".csv,.tsv,.txt,.xlsx" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} data-testid="import-file-input" />
      </label>
      <div className="flex flex-wrap gap-2">
        <a href={templateUrl('csv')} className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline" data-testid="import-template-csv"><Download size={14} /> Modèle CSV</a>
        <a href={templateUrl('xlsx')} className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline" data-testid="import-template-xlsx"><Download size={14} /> Modèle Excel</a>
      </div>
    </section>
    <section className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Colonnes reconnues</h3>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="import-fields-help">
        {(fields?.fields || []).map((f) => <li key={f.key}><span className="font-medium text-gray-800">{f.label}</span>{f.required && <span className="text-red-600"> *</span>}<span className="text-gray-500"> — {f.help}</span></li>)}
      </ul>
      <p className="text-xs text-gray-400 mt-3">* obligatoire pour créer une annonce. Les annonces importées sont créées en brouillon : vous les soumettez ensuite à la validation.</p>
    </section>
  </div>
);

// Étape 2 : aperçu + association des colonnes + profils
export const ImportMappingStep = ({ job, fields, mapping, setMapping, mode, setMode, profiles, onApplyProfile, onDeleteProfile, profileName, setProfileName, onValidate, onCancel, busy }) => {
  const used = new Set(Object.values(mapping));
  return (
    <div className="space-y-5" data-testid="import-step-mapping">
      <section className="bg-white rounded-xl shadow-sm p-5 space-y-3">
        <h3 className="font-semibold text-gray-900">2. Associez les colonnes de « {job.filename} »</h3>
        <p className="text-sm text-gray-500">{job.row_count.toLocaleString('fr-FR')} lignes détectées{job.options?.delimiter ? ` · séparateur « ${job.options.delimiter === '\t' ? 'tabulation' : job.options.delimiter} »` : ''}{job.options?.encoding ? ` · encodage ${job.options.encoding}` : ''}. Les colonnes reconnues ont été associées automatiquement.</p>
        {profiles.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="import-profiles">
            <span className="text-gray-600">Profil enregistré :</span>
            {profiles.map((p) => <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1"><button type="button" onClick={() => onApplyProfile(p)} className="hover:text-orange-600" data-testid={`import-profile-${p.id}`}>{p.name}</button><button type="button" onClick={() => onDeleteProfile(p)} aria-label="Supprimer le profil" className="text-gray-400 hover:text-red-600"><Trash2 size={12} /></button></span>)}
          </div>
        )}
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs uppercase text-gray-500 border-b"><th className="py-2 pr-3">Colonne du fichier</th><th className="py-2 pr-3">Aperçu</th><th className="py-2">Champ Otopieces</th></tr></thead>
            <tbody>
              {job.columns.map((col, i) => (
                <tr key={`${col}-${i}`} className="border-b border-gray-100" data-testid={`import-col-${i}`}>
                  <td className="py-2 pr-3 font-medium text-gray-800 break-all">{col || <em className="text-gray-400">(sans nom)</em>}</td>
                  <td className="py-2 pr-3 text-gray-500 text-xs max-w-[220px] truncate">{job.preview.slice(0, 3).map((r) => r[i]).filter(Boolean).join(' · ')}</td>
                  <td className="py-2">
                    <Select value={mapping[col] || ''} onChange={(v) => setMapping((m) => { const n = { ...m }; if (v) n[col] = v; else delete n[col]; return n; })} testId={`import-map-${i}`}>
                      <option value="">— Ne pas importer —</option>
                      {(fields?.fields || []).map((f) => <option key={f.key} value={f.key} disabled={used.has(f.key) && mapping[col] !== f.key}>{f.label}{f.required ? ' *' : ''}</option>)}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="bg-white rounded-xl shadow-sm p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mode d’import</label>
          <Select value={mode} onChange={setMode} testId="import-mode">{Object.entries(fields?.modes || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>
          <p className="text-xs text-gray-500 mt-1">La correspondance se fait uniquement sur votre « Référence vendeur », jamais sur le titre, l’EAN ou la référence OEM.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mémoriser cette correspondance (facultatif)</label>
          <div className="flex gap-2"><Input placeholder="Nom du profil" value={profileName} onChange={(e) => setProfileName(e.target.value)} data-testid="import-profile-name" /><Save size={16} className="text-gray-400 self-center" /></div>
        </div>
      </section>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onValidate} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="import-validate">{busy ? 'Validation…' : 'Valider les données'}</Button>
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="import-cancel">Annuler</Button>
      </div>
    </div>
  );
};
